import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '../firebase/config'

// Caches para mejorar el rendimiento y reducir lecturas a Firebase
const tutorProfilesCache = new Map();
const coursesCache = { data: null, lastFetch: 0 };
const CACHE_DURATION = 1000 * 60 * 5; // 5 minutos

// ========== USUARIOS ==========

// Crear o actualizar perfil de tutor
export const createOrUpdateTutorProfile = async (tutorId, profileData) => {
  try {
    const tutorRef = doc(db, 'users', tutorId)
    const tutorSnap = await getDoc(tutorRef)

    if (tutorSnap.exists()) {
      // Actualizar perfil existente
      await updateDoc(tutorRef, {
        ...profileData,
        updatedAt: serverTimestamp()
      })
    } else {
      // Crear nuevo perfil usando setDoc con el ID específico
      await setDoc(tutorRef, {
        ...profileData,
        id: tutorId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    }
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Actualizar roles de usuario y estado de administrador
export const updateUserRolesAndAdminStatus = async (userId, roles, isAdminStatus, isAccreditedStatus = false, isRetired = false, retiredData = null) => {
  try {
    const userRef = doc(db, 'users', userId)
    const legacyType = roles.includes('eucs') && !roles.includes('vtic') ? 'eucs' : 'standard'
    const updateData = {
      tutorRoles: roles,
      tutorType: legacyType,
      isAdmin: isAdminStatus,
      isAccredited: isAccreditedStatus,
      isRetired: isRetired,
      retiredData: retiredData,
      updatedAt: serverTimestamp()
    }
    await updateDoc(userRef, updateData)
    
    // Invalidar caché
    tutorProfilesCache.delete(userId)
    
    return { success: true }
  } catch (error) {
    console.error('Error updating user roles/admin status:', error)
    return { success: false, error: error.message }
  }
}

// Deshabilitar o habilitar cuenta de usuario
export const disableUserAccount = async (userId, isDisabledStatus) => {
  try {
    const userRef = doc(db, 'users', userId)
    await updateDoc(userRef, {
      isDisabled: isDisabledStatus,
      updatedAt: serverTimestamp()
    })
    tutorProfilesCache.delete(userId)
    return { success: true }
  } catch (error) {
    console.error('Error disabling user account:', error)
    return { success: false, error: error.message }
  }
}

// Borrar cuenta de usuario de Firestore
export const deleteUserAccount = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId)
    await deleteDoc(userRef)
    tutorProfilesCache.delete(userId)
    return { success: true }
  } catch (error) {
    console.error('Error deleting user account:', error)
    return { success: false, error: error.message }
  }
}


// Obtener perfil de tutor con caché
export const getTutorProfile = async (tutorId, forceRefresh = false) => {
  try {
    if (!forceRefresh && tutorProfilesCache.has(tutorId)) {
      return { success: true, data: tutorProfilesCache.get(tutorId) };
    }

    const tutorRef = doc(db, 'users', tutorId)
    const tutorSnap = await getDoc(tutorRef)

    if (tutorSnap.exists()) {
      const data = { id: tutorSnap.id, ...tutorSnap.data() };
      tutorProfilesCache.set(tutorId, data);
      return { success: true, data }
    } else {
      return { success: false, error: 'Tutor no encontrado' }
    }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Obtener perfil de tutor por slug (URL personalizada)
export const getTutorBySlug = async (slug) => {
  try {
    const q = query(collection(db, 'users'), where('slug', '==', slug))
    const querySnapshot = await getDocs(q)

    if (!querySnapshot.empty) {
      const docSnap = querySnapshot.docs[0]
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } }
    } else {
      return { success: false, error: 'Tutor no encontrado' }
    }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Verificar disponibilidad de slug
export const checkSlugAvailability = async (slug, currentUserId) => {
  try {
    const q = query(collection(db, 'users'), where('slug', '==', slug))
    const querySnapshot = await getDocs(q)

    if (querySnapshot.empty) {
      return { available: true }
    }

    // Si encontramos un documento, verificamos si es del mismo usuario
    const isSameUser = querySnapshot.docs[0].id === currentUserId
    return { available: isSameUser }
  } catch (error) {
    console.error('Error checking slug availability:', error)
    return { available: false, error: error.message }
  }
}

// Obtener todos los tutores con sus cursos
export const getAllTutors = async () => {
  try {
    // Obtener todos los usuarios (todos son tutores)
    const querySnapshot = await getDocs(collection(db, 'users'))

    // Optimización: Obtener todos los cursos una sola vez
    const coursesSnapshot = await getDocs(collection(db, 'courses'))
    const allCourses = coursesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))

    const tutors = []
    for (const docSnap of querySnapshot.docs) {
      const tutorData = { id: docSnap.id, ...docSnap.data() }

      // Filtrar cursos del tutor en memoria (Instantáneo)
      tutorData.courses = allCourses.filter(course => course.createdBy === docSnap.id)

      tutors.push(tutorData)
    }

    return { success: true, data: tutors }
  } catch (error) {
    console.error('❌ Error fetching tutors:', error)
    return { success: false, error: error.message }
  }
}

// Obtener información básica de todos los tutores (sin sus cursos)
export const getAllTutorsBasic = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'))
    const tutors = []
    querySnapshot.forEach((doc) => {
      tutors.push({ id: doc.id, ...doc.data() })
    })
    return { success: true, data: tutors }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Buscar tutor por nombre
export const findTutorByName = async (name) => {
  try {
    const q = query(collection(db, 'users'), where('fullName', '==', name))
    const querySnapshot = await getDocs(q)

    if (!querySnapshot.empty) {
      const tutorDoc = querySnapshot.docs[0]
      return { success: true, data: { id: tutorDoc.id, ...tutorDoc.data() } }
    } else {
      return { success: false, error: 'Tutor no encontrado' }
    }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Escuchar cambios en perfil de tutor
export const subscribeToTutorProfile = (tutorId, callback) => {
  const tutorRef = doc(db, 'users', tutorId)
  return onSnapshot(tutorRef, (doc) => {
    if (doc.exists()) {
      callback({ success: true, data: doc.data() })
    } else {
      callback({ success: false, error: 'Tutor no encontrado' })
    }
  })
}

// ========== CURSOS ==========

// Crear curso
export const createCourse = async (courseData, tutorId) => {
  try {
    const courseRef = await addDoc(collection(db, 'courses'), {
      ...courseData,
      tutors: [tutorId],
      createdBy: tutorId,
      createdAt: serverTimestamp()
    })
    return { success: true, courseId: courseRef.id }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Obtener todos los cursos con caché
export const getAllCourses = async (forceRefresh = false) => {
  try {
    const now = Date.now();
    if (!forceRefresh && coursesCache.data && (now - coursesCache.lastFetch < CACHE_DURATION)) {
      return { success: true, data: coursesCache.data };
    }

    const coursesSnapshot = await getDocs(collection(db, 'courses'))
    const courses = []
    coursesSnapshot.forEach((doc) => {
      courses.push({ id: doc.id, ...doc.data() })
    })

    coursesCache.data = courses;
    coursesCache.lastFetch = now;

    return { success: true, data: courses }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Obtener curso por ID
export const getCourseById = async (courseId) => {
  try {
    const courseRef = doc(db, 'courses', courseId)
    const courseSnap = await getDoc(courseRef)

    if (courseSnap.exists()) {
      return { success: true, data: { id: courseSnap.id, ...courseSnap.data() } }
    } else {
      return { success: false, error: 'Curso no encontrado' }
    }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Obtener cursos de un tutor
export const getTutorCourses = async (tutorId) => {
  try {
    const q = query(collection(db, 'courses'), where('tutors', 'array-contains', tutorId))
    const querySnapshot = await getDocs(q)
    
    // Cargar cada curso con sus secciones en paralelo para mayor velocidad
    const coursesPromises = querySnapshot.docs.map(async (doc) => {
      const courseData = { id: doc.id, ...doc.data() }

      const sectionsQuery = query(
        collection(db, 'sections'),
        where('courseId', '==', doc.id)
      )
      const sectionsSnapshot = await getDocs(sectionsQuery)
      const sections = []
      sectionsSnapshot.forEach((sectionDoc) => {
        sections.push({ id: sectionDoc.id, ...sectionDoc.data() })
      })

      courseData.sections = sections
      return courseData
    })

    const courses = await Promise.all(coursesPromises)

    return { success: true, data: courses }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Actualizar curso
export const updateCourse = async (courseId, courseData) => {
  try {
    const courseRef = doc(db, 'courses', courseId)
    await updateDoc(courseRef, {
      ...courseData,
      updatedAt: serverTimestamp()
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Unirse a curso (agregar tutor al array)
export const joinCourse = async (courseId, tutorId) => {
  try {
    const courseRef = doc(db, 'courses', courseId)
    await updateDoc(courseRef, {
      tutors: arrayUnion(tutorId)
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Abandonar o eliminar curso
export const deleteCourse = async (courseId, tutorId) => {
  if (!tutorId) return { success: false, error: 'Se requiere ID del tutor' }

  try {
    const courseRef = doc(db, 'courses', courseId)
    const courseSnap = await getDoc(courseRef)

    if (!courseSnap.exists()) {
      return { success: false, error: 'Curso no encontrado' }
    }

    const courseData = courseSnap.data()
    const tutors = courseData.tutors || []

    // Si hay más de un tutor, solo nos eliminamos de la lista
    if (tutors.length > 1) {
      await updateDoc(courseRef, {
        tutors: arrayRemove(tutorId)
      })
      return { success: true, message: 'Has abandonado el curso' }
    }

    // Si somos el último tutor, borramos TODO el rastro del curso

    // 1. Borrar todas las secciones asociadas
    const sectionsQuery = query(collection(db, 'sections'), where('courseId', '==', courseId))
    const sectionsSnap = await getDocs(sectionsQuery)
    const sectionDeletePromises = sectionsSnap.docs.map(d => deleteDoc(doc(db, 'sections', d.id)))
    await Promise.all(sectionDeletePromises)

    // 2. Borrar todos los materiales
    const materialsQuery = query(collection(db, 'course_materials'), where('courseId', '==', courseId))
    const materialsSnap = await getDocs(materialsQuery)
    const materialsDeletePromises = materialsSnap.docs.map(d => deleteDoc(doc(db, 'course_materials', d.id)))
    await Promise.all(materialsDeletePromises)

    // 3. Borrar todas las notas
    const notesQuery = query(collection(db, 'course_notes'), where('courseId', '==', courseId))
    const notesSnap = await getDocs(notesQuery)
    const notesDeletePromises = notesSnap.docs.map(d => deleteDoc(doc(db, 'course_notes', d.id)))
    await Promise.all(notesDeletePromises)

    // 4. Finalmente, borrar el curso principal
    await deleteDoc(courseRef)

    return { success: true, message: 'Curso finalizado y eliminado completamente' }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Escuchar cambios en cursos
export const subscribeToCourses = (callback) => {
  return onSnapshot(collection(db, 'courses'), (snapshot) => {
    const courses = []
    snapshot.forEach((doc) => {
      courses.push({ id: doc.id, ...doc.data() })
    })
    callback({ success: true, data: courses })
  })
}

// ========== SECCIONES ==========

// Crear sección
export const createSection = async (sectionData, courseId, tutorId) => {
  try {
    const sectionRef = await addDoc(collection(db, 'sections'), {
      ...sectionData,
      courseId,
      createdBy: tutorId,
      createdAt: serverTimestamp()
    })
    return { success: true, sectionId: sectionRef.id }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Obtener secciones de un curso
export const getCourseSections = async (courseId) => {
  try {
    const q = query(collection(db, 'sections'), where('courseId', '==', courseId))
    const querySnapshot = await getDocs(q)
    const sections = []
    querySnapshot.forEach((doc) => {
      sections.push({ id: doc.id, ...doc.data() })
    })
    return { success: true, data: sections }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Obtener secciones de un tutor
export const getTutorSections = async (tutorId) => {
  try {
    const q = query(collection(db, 'sections'), where('createdBy', '==', tutorId))
    const querySnapshot = await getDocs(q)
    const sections = []
    querySnapshot.forEach((doc) => {
      sections.push({ id: doc.id, ...doc.data() })
    })
    return { success: true, data: sections }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Obtener todas las secciones
export const getAllSections = async () => {
  try {
    const sectionsSnapshot = await getDocs(collection(db, 'sections'))
    const sections = []
    sectionsSnapshot.forEach((doc) => {
      sections.push({ id: doc.id, ...doc.data() })
    })
    return { success: true, data: sections }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Actualizar sección
export const updateSection = async (sectionId, sectionData) => {
  try {
    const sectionRef = doc(db, 'sections', sectionId)
    await updateDoc(sectionRef, {
      ...sectionData,
      updatedAt: serverTimestamp()
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Eliminar sección
export const deleteSection = async (sectionId) => {
  try {
    await deleteDoc(doc(db, 'sections', sectionId))
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Escuchar cambios en secciones de un curso
export const subscribeToCourseSections = (courseId, callback) => {
  const q = query(collection(db, 'sections'), where('courseId', '==', courseId))
  return onSnapshot(q, (snapshot) => {
    const sections = []
    snapshot.forEach((doc) => {
      sections.push({ id: doc.id, ...doc.data() })
    })
    callback({ success: true, data: sections })
  })
}

// ========== MATERIALES ==========

// Crear material
export const createMaterial = async (materialData, courseId, tutorId) => {
  try {
    const materialRef = await addDoc(collection(db, 'materials'), {
      ...materialData,
      courseId,
      createdBy: tutorId,
      createdAt: serverTimestamp(),
      order: materialData.order || 0
    })
    return { success: true, materialId: materialRef.id }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Obtener materiales de un curso
export const getCourseMaterials = async (courseId) => {
  try {
    const q = query(
      collection(db, 'materials'),
      where('courseId', '==', courseId)
    )
    const querySnapshot = await getDocs(q)
    const materials = []
    querySnapshot.forEach((doc) => {
      materials.push({ id: doc.id, ...doc.data() })
    })
    // Ordenar por campo order
    materials.sort((a, b) => (a.order || 0) - (b.order || 0))
    return { success: true, data: materials }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Actualizar material
export const updateMaterial = async (materialId, materialData) => {
  try {
    const materialRef = doc(db, 'materials', materialId)
    await updateDoc(materialRef, {
      ...materialData,
      updatedAt: serverTimestamp()
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Eliminar material
export const deleteMaterial = async (materialId) => {
  try {
    await deleteDoc(doc(db, 'materials', materialId))
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Escuchar cambios en materiales de un curso
export const subscribeToCourseMaterials = (courseId, callback) => {
  const q = query(collection(db, 'materials'), where('courseId', '==', courseId))
  return onSnapshot(q, (snapshot) => {
    const materials = []
    snapshot.forEach((doc) => {
      materials.push({ id: doc.id, ...doc.data() })
    })
    materials.sort((a, b) => (a.order || 0) - (b.order || 0))
    callback({ success: true, data: materials })
  })
}

// ========== NOTAS DEL CURSO ==========

// Crear nota
export const createCourseNote = async (noteData, courseId, tutorId) => {
  try {
    const noteRef = await addDoc(collection(db, 'course_notes'), {
      ...noteData,
      courseId,
      createdBy: tutorId,
      createdAt: serverTimestamp(),
      pinned: noteData.pinned || false
    })
    return { success: true, noteId: noteRef.id }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Obtener notas de un curso
export const getCourseNotes = async (courseId) => {
  try {
    const q = query(
      collection(db, 'course_notes'),
      where('courseId', '==', courseId)
    )
    const querySnapshot = await getDocs(q)
    const notes = []
    querySnapshot.forEach((doc) => {
      notes.push({ id: doc.id, ...doc.data() })
    })
    // Ordenar: primero las fijadas, luego por fecha
    notes.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1
      if (!a.pinned && b.pinned) return 1
      return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
    })
    return { success: true, data: notes }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Actualizar nota
export const updateCourseNote = async (noteId, noteData) => {
  try {
    const noteRef = doc(db, 'course_notes', noteId)
    await updateDoc(noteRef, {
      ...noteData,
      updatedAt: serverTimestamp()
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Eliminar nota
export const deleteCourseNote = async (noteId) => {
  try {
    await deleteDoc(doc(db, 'course_notes', noteId))
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Escuchar cambios en notas de un curso
export const subscribeToCourseNotes = (courseId, callback) => {
  const q = query(
    collection(db, 'course_notes'),
    where('courseId', '==', courseId)
  )

  return onSnapshot(q, (snapshot) => {
    const notes = []
    snapshot.forEach((doc) => {
      notes.push({ id: doc.id, ...doc.data() })
    })

    // Ordenar: primero las fijadas, luego por fecha (más recientes primero)
    notes.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1
      if (!a.pinned && b.pinned) return 1
      return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
    })

    callback({ success: true, data: notes })
  }, (error) => {
    callback({ success: false, error: error.message })
  })
}

// ========== MURO (WALL POSTS) ==========

// Crear una publicación en el muro
export const createWallPost = async (postData, tutorId) => {
  try {
    const postRef = await addDoc(collection(db, 'wall_posts'), {
      ...postData,
      createdBy: tutorId,
      createdAt: serverTimestamp(),
      likes: []
    })
    return { success: true, postId: postRef.id }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Escuchar cambios en publicaciones del muro (en tiempo real)
export const subscribeToWallPosts = (callback) => {
  const q = query(collection(db, 'wall_posts'))

  return onSnapshot(q, (snapshot) => {
    const posts = []
    snapshot.forEach((doc) => {
      posts.push({ id: doc.id, ...doc.data() })
    })

    // Ordenar por fecha (más recientes primero)
    posts.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    callback({ success: true, data: posts })
  }, (error) => {
    callback({ success: false, error: error.message })
  })
}

// Eliminar publicación del muro
export const deleteWallPost = async (postId) => {
  try {
    await deleteDoc(doc(db, 'wall_posts', postId))
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// ========== CONFIGURACIÓN DEL CURSO ==========

// Actualizar configuración del curso
export const updateCourseSettings = async (courseId, settings) => {
  try {
    const courseRef = doc(db, 'courses', courseId)
    await updateDoc(courseRef, {
      settings: settings,
      updatedAt: serverTimestamp()
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// ========== GRUPOS DE INVESTIGACIÓN ==========

// Crear un grupo de investigación
export const createResearchGroup = async (groupData, creatorId) => {
  try {
    const groupRef = await addDoc(collection(db, 'research_groups'), {
      ...groupData,
      createdBy: creatorId,
      members: [creatorId], // El creador es miembro automáticamente
      createdAt: serverTimestamp()
    })
    
    // Añadir el grupo al perfil del creador
    const userRef = doc(db, 'users', creatorId)
    await updateDoc(userRef, {
      researchGroups: arrayUnion(groupRef.id)
    })
    
    return { success: true, groupId: groupRef.id }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Obtener todos los grupos de investigación
export const getAllResearchGroups = async () => {
  try {
    const q = query(collection(db, 'research_groups'))
    const querySnapshot = await getDocs(q)
    const groups = []
    
    // Obtener nombres de creadores
    const creatorIds = [...new Set(querySnapshot.docs.map(doc => doc.data().createdBy))]
    const creatorsMap = {}
    
    if (creatorIds.length > 0) {
      // Optimización: si son menos de 10, podemos usar 'in'
      if (creatorIds.length <= 10) {
        const usersQ = query(collection(db, 'users'), where('__name__', 'in', creatorIds))
        const usersSnap = await getDocs(usersQ)
        usersSnap.forEach(uDoc => {
          creatorsMap[uDoc.id] = uDoc.data().fullName || uDoc.data().email
        })
      } else {
        // En un caso real, usaríamos Promise.all o un backend, aquí lo simulamos por simplicidad
        const usersSnap = await getDocs(collection(db, 'users'))
        usersSnap.forEach(uDoc => {
          if (creatorIds.includes(uDoc.id)) {
            creatorsMap[uDoc.id] = uDoc.data().fullName || uDoc.data().email
          }
        })
      }
    }

    querySnapshot.forEach((doc) => {
      const data = doc.data()
      // Removemos el keyword del frontend por seguridad al listar
      const safeData = { ...data }
      delete safeData.keyword 
      
      groups.push({
        id: doc.id,
        ...safeData,
        creatorName: creatorsMap[data.createdBy] || 'Tutor Desconocido'
      })
    })
    
    // Odenar alfabéticamente
    groups.sort((a, b) => a.name.localeCompare(b.name))
    
    return { success: true, data: groups }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Unirse a un grupo de investigación con palabra clave
export const joinResearchGroup = async (groupId, keywordToTest, userId) => {
  try {
    const groupRef = doc(db, 'research_groups', groupId)
    const groupSnap = await getDoc(groupRef)
    
    if (!groupSnap.exists()) {
      return { success: false, error: 'El grupo de investigación no existe.' }
    }
    
    const groupData = groupSnap.data()
    
    if (groupData.keyword !== keywordToTest) {
      return { success: false, error: 'Palabra clave incorrecta.' }
    }
    
    // Validar si ya es miembro
    if (groupData.members && groupData.members.includes(userId)) {
      return { success: false, error: 'Ya eres miembro de este grupo.' }
    }
    
    // Añadir usuario a los miembros del grupo
    await updateDoc(groupRef, {
      members: arrayUnion(userId)
    })
    
    // Añadir grupo al perfil del usuario
    const userRef = doc(db, 'users', userId)
    await updateDoc(userRef, {
      researchGroups: arrayUnion(groupId)
    })
    
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Obtener los grupos de investigación de un usuario
export const getUserResearchGroups = async (userId) => {
  try {
    const q = query(
      collection(db, 'research_groups'),
      where('members', 'array-contains', userId)
    )
    const querySnapshot = await getDocs(q)
    const groups = []
    
    querySnapshot.forEach((doc) => {
      const data = doc.data()
      delete data.keyword // Seguridad: No mandar clave al nav front
      groups.push({ id: doc.id, ...data })
    })
    
    return { success: true, data: groups }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Actualizar los datos ampliados de un grupo de investigación
export const updateResearchGroup = async (groupId, groupData) => {
  try {
    const groupRef = doc(db, 'research_groups', groupId)
    
    await updateDoc(groupRef, {
      ...groupData,
      updatedAt: serverTimestamp()
    })
    
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Obtener un grupo de investigación por ID
export const getResearchGroupById = async (groupId) => {
  try {
    const groupRef = doc(db, 'research_groups', groupId)
    const groupSnap = await getDoc(groupRef)
    
    if (!groupSnap.exists()) {
      return { success: false, error: 'Grupo no encontrado' }
    }
    
    const data = groupSnap.data()
    delete data.keyword // Seguridad

    // Obtener nombres de los miembros (Optimización simple para el perfil del grupo)
    // En una DB grande se haría paginado, aquí como son pocos miembros, podemos traerlos directamente.
    let membersData = []
    if (data.members && data.members.length > 0) {
      const usersRef = collection(db, 'users')
      // Si son más de 10, limitamos a los primeros 10 en visualización o usaríamos un query diferente. 
      // Por practicidad de Firebase, traemos de a batches o todos los usuarios 
      // y filtramos. Usaremos la estrategia de getDocs + filter ya que es un demo.
      const usersSnap = await getDocs(usersRef)
      usersSnap.forEach(uDoc => {
        if (data.members.includes(uDoc.id)) {
          membersData.push({
            id: uDoc.id,
            fullName: uDoc.data().fullName || uDoc.data().email,
            email: uDoc.data().email,
            photoURL: uDoc.data().photoURL || uDoc.data().avatar || ''
          })
        }
      })
    }

    return { 
      success: true, 
      data: {
        id: groupSnap.id,
        ...data,
        membersData
      } 
    }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// Eliminar un grupo de investigación (Solo el propietario)
export const deleteResearchGroup = async (groupId, userId) => {
  try {
    const groupRef = doc(db, 'research_groups', groupId)
    const groupSnap = await getDoc(groupRef)
    
    if (!groupSnap.exists()) {
       return { success: false, error: 'Grupo no encontrado.' }
    }
    
    const groupData = groupSnap.data()
    if (groupData.createdBy !== userId) {
       return { success: false, error: 'No tienes permiso para eliminar este grupo.' }
    }
    
    await deleteDoc(groupRef)
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}
